import { FieldArray, Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
// import { AppContext } from '~/App';
import { FindAddContactField } from '~/components/FindAddContact';
import { AddressInputGroup } from '~/components/form/AddressInputGroup';
import { StatusToggle } from '~/components/form/StatusToggle';
import { SubmitButton } from '~/components/form/SubmitButton';
import { TextField } from '~/components/form/TextField';
import { SingleUploadField, UploadResult } from '~/components/upload2/Upload';
import {
  SiteInput,
  Status,
  usePlaceTypeConfigQuery,
  useSiteTypesQuery,
  useSitesQuery,
} from '~/generated/graphql';
import { getFragmentData } from '~/gql';
import { SiteQuery } from '~/gql/graphql';
import { parseJson } from '~/helpers';
import {
  convertAssetImagesToUploadResults,
  uploadResultToImageString,
} from '~/helpers/imageConversion';
// import { useLocalStorage } from '~/hooks';
import { SideLayout } from '~/layouts/side/SideLayout';
import { PromptEffect } from '../Prompt';
import { Button } from '../form/SubmitButton';
import { TimePickerField } from '../form/TimePicker';
import { ComboboxField } from '../ui/Combobox';
import { Label } from '../ui/Label';
import { AttributeFieldsFragment } from './tabs/AttributesList';

type Props = {
  title: string;
  site?: SiteQuery['site'];
  onClose: (siteId?: string) => void;
  onSubmit: (input: SiteInput) => void;
};

// Define a type for the form's internal values, ensuring images is UploadResult[]
type SiteFormValues = Omit<SiteInput, 'images'> & {
  images: UploadResult[];
};

export const SiteForm = ({ title, site, onClose, onSubmit }: Props) => {
  const { t } = useTranslation();

  const attributes = getFragmentData(AttributeFieldsFragment, site?.attributes);

  // Note: attachments map removed as it was unused

  // Use SiteFormValues for initial values type
  const initialValues: SiteFormValues = {
    status: site?.status ?? Status.Active,
    name: site?.name ?? '',
    type: site?.type ?? '',
    address: site?.address ? parseJson(site.address) : null,
    defaultSourceId: site?.defaultSource?.id ?? null,
    licensorId: site?.licensor?.id ?? null,
    notes: site?.notes ?? '',
    // TODO do not submit attribute values with create/update site at all
    attributes:
      attributes
        ?.filter(({ id }) => !id.startsWith('integration_'))
        .map(({ id, value, attachments }) => ({
          id,
          name: id,
          value,
          attachments: attachments?.map((saved) => ({
            id: saved.id,
            kind: saved.kind,
            originalFilename: saved.originalFilename,
            preview: saved.thumb,
          })),
        })) ?? [],
    images: convertAssetImagesToUploadResults(site),
    airbnbCalendar: (site?.airbnbCalendar?.map(({ url }) => url) ?? [
      '',
    ]) as string[],
    airbnbDefaultCheckIn: site?.airbnbDefaultCheckIn ?? '',
    airbnbDefaultCheckOut: site?.airbnbDefaultCheckOut ?? '',
  };

  const validationSchema = () => {
    return Yup.object().shape({
      name: Yup.string().required('Please complete this required field'),
      // .test('name-exists', 'Please enter a unique name', checkNameIsUnique),
      airbnbCalendar: Yup.array().of(
        Yup.string().matches(
          /^https:\/\/(?:www\.airbnb\.com\.au\/calendar\/ical\/(\d+)\.ics\?s=([0-9a-f]+)|admin.booking.com\/hotel\/hoteladmin\/ical.html\?t=[0-9a-f-]+|app\.guesty\.com\/api\/public\/icalendar-dashboard-api\/export\/[0-9a-f-]+|api\.homhero\.com\.au\/ical\/.+)$/,
          'Invalid calendar URL'
        )
      ),
      // address_data: Yup.object().shape({
      //   subpremise: Yup.string()
      //     .min(1)
      //     .required('Please complete this required field'),
      //   street_number: Yup.string()
      //     .min(1)
      //     .required('Please complete this required field'),
      //   route: Yup.string()
      //     .min(1)
      //     .required('Please complete this required field'),
      //   locality: Yup.string()
      //     .min(1)
      //     .required('Please complete this required field'),
      //   administrative_area_level_1: Yup.string()
      //     .min(1)
      //     .required('Please complete this required field'),
      //   postal_code: Yup.string()
      //     .min(1)
      //     .required('Please complete this required field'),
      //   country: Yup.string()
      //     .min(1)
      //     .required('Please complete this required field'),
      // }),
    });
  };

  function handleClose() {
    onClose(site?.id);
  }

  async function handleSubmit(formValues: SiteFormValues) {
    const siteInputToSubmit: SiteInput = {
      ...formValues,
      images: formValues.images.map(uploadResultToImageString),
      address: JSON.stringify(formValues.address),
      attributes: formValues.attributes?.map(({ name, value }) => ({
        name,
        value,
      })),
    };
    return onSubmit(siteInputToSubmit);
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ values, errors }) => (
        <SideLayout>
          <PromptEffect cancel='Keep Editing' />
          <SideLayout.Head onClose={handleClose}>
            {title && t(title)}
          </SideLayout.Head>
          <SideLayout.Body>
            <StatusToggle />

            <TextField name='name' label={t('siteName')} required />

            <SiteTypeField />

            <AddressInputGroup name='address' label='Address' />

            <FindAddContactField
              label={t('licensor')}
              name='licensorId'
              type='Owner'
            />

            {/* <SelectField
            label={t('defaultSource')}
            name='defaultSourceId'
            options={siteOptions}
          /> */}

            {site?.id && (
              <>
                <Label>
                  {t('calendar', { count: values.airbnbCalendar?.length ?? 1 })}
                </Label>
                <FieldArray
                  name='airbnbCalendar'
                  render={(arrayHelpers) => (
                    <div className='mb-5'>
                      {values.airbnbCalendar &&
                        values.airbnbCalendar.length > 0 &&
                        values.airbnbCalendar.map((url, index) => (
                          <TextField
                            key={index}
                            name={`airbnbCalendar.${index}`}
                            label=''
                            className='mb-2'
                            placeholder='Paste ical url here'
                          />
                        ))}
                      {values.airbnbCalendar &&
                        values.airbnbCalendar[
                          values.airbnbCalendar.length - 1
                        ] !== '' && (
                          <Button
                            type='button'
                            intent='text'
                            size='small'
                            onClick={() => arrayHelpers.push('')}
                          >
                            Add another calendar
                          </Button>
                        )}
                    </div>
                  )}
                />
              </>
            )}

            {values.airbnbCalendar?.length && !errors.airbnbCalendar && (
              <div className='flex gap-5'>
                <TimePickerField
                  label='Default check-in'
                  name='airbnbDefaultCheckIn'
                  focusValue='15:00'
                />
                <TimePickerField
                  label='Default check-out'
                  name='airbnbDefaultCheckOut'
                  focusValue='10:00'
                />
              </div>
            )}

            <TextField name='notes' label='Notes' />

            {/* Use SingleUploadField for one image */}
            <SingleUploadField
              id='siteImage'
              name='images'
              imagesOnly={true}
              isMainUpload={true}
            />
          </SideLayout.Body>
          <SideLayout.Foot className='p-4'>
            <SubmitButton />
          </SideLayout.Foot>
        </SideLayout>
      )}
    </Formik>
  );
};

function SiteTypeField() {
  const { t } = useTranslation();

  // Preset types (as used in ManagePlaceTypes for known list)
  const [presetTypesResult] = useSiteTypesQuery({
    variables: { preset: true },
  });
  const serverPresetTypes = useMemo(
    () => presetTypesResult.data?.siteTypes ?? [],
    [presetTypesResult.data?.siteTypes]
  );

  // All sites to gather any types in use
  const [allSitesResult] = useSitesQuery();
  const typesInUse = useMemo(
    () =>
      (
        (allSitesResult.data?.sites ?? [])
          .map((s) => s.type)
          .filter(Boolean) as string[]
      ).filter((v, i, a) => a.indexOf(v) === i),
    [allSitesResult.data]
  );

  // Load server place type config to determine ordering
  const [configResult] = usePlaceTypeConfigQuery({
    requestPolicy: 'cache-and-network',
  });

  const serverTypes = useMemo(
    () => configResult.data?.placeTypeConfig?.types ?? [],
    [configResult.data?.placeTypeConfig?.types]
  );

  const uniqueSorted = (values: string[]) => {
    const normalized = values
      .map((v) => (typeof v === 'string' ? v.trim() : v))
      .filter((v): v is string => !!v);
    const deduped = Array.from(new Set(normalized));
    return deduped
      .filter((t) => t.toLowerCase() !== 'other')
      .sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
      );
  };

  const knownTypes = useMemo(
    () => uniqueSorted([...serverPresetTypes, ...typesInUse]),
    [serverPresetTypes, typesInUse]
  );

  const orderedTypes = useMemo(() => {
    if (serverTypes.length > 0) {
      // Match ManagePlaceTypes: always respect server-provided order,
      // and append any extras not present on the server list.
      const extras = knownTypes.filter((t) => !serverTypes.includes(t));
      return [...serverTypes, ...extras];
    }
    return knownTypes;
  }, [serverTypes, knownTypes]);

  return (
    <ComboboxField
      name='type'
      label={t('siteType')}
      items={orderedTypes}
      sortItems={false}
    />
  );
}
